import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { LeadDisqualificationReasonApiService } from '../services/lead-disqualification-reason-api.service';
import { LeadDisqualificationReasonRequest, LeadDisqualificationReasonResponse } from '../models/lead-disqualification-reason.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-lead-disqualification-reason-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './lead-disqualification-reason-form.component.html'
})
export class LeadDisqualificationReasonFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  reasonId: number | null = null;

  checkingDuplicate = false;
  duplicateNameError = '';
  nameValidationError = '';
  descriptionValidationError = '';

  readonly NAME_MAX = 30;

  // Form model
  name = '';
  private originalName = '';
  description = '';
  autoReschedule = false;
  defaultRetryDays: number | null = null;
  isActive = true;

  constructor(
    private reasonApi: LeadDisqualificationReasonApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.reasonId = +id;
      this.cargarRazon(this.reasonId);
    }
  }

  private cargarRazon(id: number): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.reasonApi.getReasonById(id, businessId).subscribe({
      next: (razon) => {
        this.name = razon.name;
        this.originalName = razon.name;
        this.description = razon.description || '';
        this.autoReschedule = razon.autoReschedule;
        this.defaultRetryDays = razon.defaultRetryDays ?? null;
        this.isActive = razon.isActive;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la razón';
        this.loading = false;
      }
    });
  }

  onNameChange(value: string): void {
    let filtered = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
    if (filtered.length > this.NAME_MAX) {
      filtered = filtered.substring(0, this.NAME_MAX);
      this.nameValidationError = 'Máximo 30 caracteres';
    } else if (filtered !== value) {
      this.nameValidationError = 'Solo se permiten letras';
    } else {
      this.nameValidationError = '';
    }
    setTimeout(() => { this.name = filtered; });
  }

  onDescriptionChange(value: string): void {
    const filtered = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
    if (filtered !== value) {
      this.descriptionValidationError = 'Solo se permiten letras';
    } else {
      this.descriptionValidationError = '';
    }
    setTimeout(() => { this.description = filtered; });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Razón de Descalificación' : 'Nueva Razón de Descalificación';
  }

  guardar(): void {
    if (!this.name.trim()) {
      this.error = 'El nombre es requerido';
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    this.error = '';
    this.duplicateNameError = '';
    this.nameValidationError = '';
    this.descriptionValidationError = '';
    this.checkingDuplicate = true;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.reasonApi.getReasons(businessId).pipe(
      map(data => data || []),
      catchError(() => of([] as LeadDisqualificationReasonResponse[]))
    ).subscribe(existingReasons => {
      this.checkingDuplicate = false;

      const normalizedName = this.name.trim().toLowerCase();
      const duplicateName = existingReasons.find(r =>
        r.name.trim().toLowerCase() === normalizedName &&
        (!this.isEditing || r.id !== this.reasonId)
      );

      if (duplicateName) {
        this.duplicateNameError = `Ya existe una razón con el nombre "${duplicateName.name}"`;
        return;
      }

      this.executeSave();
    });
  }

  private executeSave(): void {
    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: LeadDisqualificationReasonRequest = {
      businessId,
      name: this.name.trim(),
      description: this.description.trim() || undefined,
      autoReschedule: this.autoReschedule,
      defaultRetryDays: this.defaultRetryDays ?? undefined,
      isActive: this.isActive
    };

    if (this.isEditing && this.reasonId) {
      this.reasonApi.updateReason(this.reasonId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Razón de descalificación',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-leads/razones-descalificacion']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar la razón';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.reasonApi.createReason(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Razón de descalificación', `Se creó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-leads/razones-descalificacion']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear la razón';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-leads/razones-descalificacion']);
  }
}
